import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import type { Database } from '@/types/database'
import type { UserRole } from '@/types/admin'

type Profile = Database['public']['Tables']['profiles']['Row']

interface AuthState {
  user: Profile | null
  isLoading: boolean
  isAuthenticated: boolean
  role: UserRole
  setUser: (user: Profile | null) => void
  setLoading: (loading: boolean) => void
  setRole: (role: UserRole) => void
  signInWithGoogle: () => Promise<{ error: string | null }>
  signOut: () => Promise<void>
  fetchProfile: () => Promise<void>
  updateNsfwSetting: (showNsfwContent: boolean) => Promise<void>
  updateHideNsfwSetting: (hideNsfwContent: boolean) => Promise<void>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isLoading: true,
      isAuthenticated: false,
      role: 'user',

      setUser: (user) => set({ user, isAuthenticated: !!user }),
      setLoading: (isLoading) => set({ isLoading }),
      setRole: (role) => set({ role }),

      signInWithGoogle: async () => {
        const { data, error } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo: `${window.location.origin}/auth/callback`,
          },
        })

        if (error) {
          return { error: error.message }
        }

        return { error: null }
      },

      signOut: async () => {
        await supabase.auth.signOut()
        set({ user: null, isAuthenticated: false, role: 'user' })
      },

      fetchProfile: async () => {
        const resolveAuthUser = async (): Promise<User | null> => {
          const { data: { session } } = await supabase.auth.getSession()
          if (session?.user) return session.user
          const { data: { user } } = await supabase.auth.getUser()
          return user ?? null
        }

        let authUser = await resolveAuthUser()
        if (!authUser) {
          await new Promise((r) => setTimeout(r, 200))
          authUser = await resolveAuthUser()
        }

        if (!authUser) {
          set({ user: null, isAuthenticated: false, isLoading: false, role: 'user' })
          return
        }

        const { data: profile, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', authUser.id)
          .single()

        if (error) {
          // If it's a "not found" error, the profile might not be created yet
          // but we should still consider the user authenticated (they logged in via auth)
          if (error.code === 'PGRST116') {
            // Profile doesn't exist yet - create one or handle appropriately
            set({ user: null, isAuthenticated: true, isLoading: false, role: 'user' })
          } else if (error.code === '500' || (error as any).status === 500) {
            // Server error - don't log out the user, keep them authenticated based on auth session
            set({ user: null, isAuthenticated: true, isLoading: false, role: 'user' })
          } else {
            set({ user: null, isAuthenticated: false, isLoading: false, role: 'user' })
          }
          return
        }

        if (!profile) {
          set({ user: null, isAuthenticated: false, isLoading: false, role: 'user' })
          return
        }

        // Get role directly from the profile table
        const role = (profile.role as UserRole) || 'user'
        set({ user: profile, isAuthenticated: true, isLoading: false, role })
      },

      updateNsfwSetting: async (showNsfwContent: boolean) => {
        const { user } = useAuthStore.getState()
        if (!user) return
        const { error } = await supabase
          .from('profiles')
          .update({ show_nsfw_content: showNsfwContent })
          .eq('user_id', user.user_id)
        if (!error) {
          useAuthStore.setState({ user: { ...user, show_nsfw_content: showNsfwContent } })
        }
      },

      updateHideNsfwSetting: async (hideNsfwContent: boolean) => {
        const { user } = useAuthStore.getState()
        if (!user) return
        const { error } = await supabase
          .from('profiles')
          .update({ hide_nsfw_content: hideNsfwContent })
          .eq('user_id', user.user_id)
        if (!error) {
          useAuthStore.setState({ user: { ...user, hide_nsfw_content: hideNsfwContent } })
        }
      },
    }),
    {
      name: 'comfyflow-auth',
      partialize: (state) => ({ user: state.user, isAuthenticated: state.isAuthenticated, role: state.role }),
    }
  )
)

export const initializeAuth = async () => {
  const store = useAuthStore.getState()
  store.setLoading(true)

  try {
    await store.fetchProfile()
  } catch {
    store.setLoading(false)
  }
}

function waitForAuthStoreHydration(): Promise<void> {
  return new Promise((resolve) => {
    const api = useAuthStore.persist
    if (api.hasHydrated()) {
      resolve()
      return
    }
    const unsub = api.onFinishHydration(() => {
      unsub()
      resolve()
    })
  })
}

/** Single flight per page load (handles React Strict Mode double-invoke). */
let clientAuthBootstrap: Promise<void> | null = null

export function bootstrapClientAuthOnce(): Promise<void> {
  clientAuthBootstrap ??= (async () => {
    await waitForAuthStoreHydration()
    await initializeAuth()
  })()
  return clientAuthBootstrap
}

export const isAdmin = () => {
  const { role } = useAuthStore.getState()
  return role === 'admin' || role === 'moderator'
}

/** Platform administrator only (`profiles.role = 'admin'`), not moderators. */
export const isPlatformAdmin = () => {
  const { role } = useAuthStore.getState()
  return role === 'admin'
}

export const isModerator = () => {
  const { role } = useAuthStore.getState()
  return role === 'moderator' || role === 'admin'
}